import { useQuery } from '@tanstack/react-query';
import { api, GrClaimRejectionEntry } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseClaimRejectionsOptions {
  archeId?: string;
  attemptedGameName?: string;
  /** Player-targeting filter — UI sends username; server resolves to userId. */
  username?: string;
  sinceHours?: number;
  limit?: number;
}

interface UseClaimRejectionsReturn {
  rejections: GrClaimRejectionEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Recent claim-rejection log entries for the AdminGamesPage panel. Surfaces
 * what arches/apps tried to claim and why they were rejected so platform
 * admins can register a matching gr_game_configs row when needed.
 */
export function useClaimRejections(options?: UseClaimRejectionsOptions): UseClaimRejectionsReturn {
  const filters = {
    archeId: options?.archeId,
    attemptedGameName: options?.attemptedGameName,
    username: options?.username,
    sinceHours: options?.sinceHours,
    limit: options?.limit ?? 25,
  };
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.gameRewards.claimRejections(filters),
    queryFn: () => api.listGameRewardsRejections(filters),
    staleTime: 30 * 1000, // rejections trickle in; refresh frequently when admin is viewing
  });

  return {
    rejections: data?.rejections ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error instanceof Error ? error.message : isError ? 'Failed to load rejections' : null,
    refresh: () => {
      refetch();
    },
  };
}
